import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const DEFAULT_PRIVACY_TITLE = "Políticas de privacidad";
const DEFAULT_PRIVACY_CONTENT = `Última actualización: febrero de 2026.

En Sur Primicia ("el portal", "nosotros") respetamos la privacidad de las personas que visitan nuestro sitio y utilizan nuestros servicios digitales. Esta política explica qué datos recopilamos, con qué finalidad, cómo los protegemos y qué derechos tenés sobre ellos, en el marco de la Ley 25.326 de Protección de Datos Personales de la República Argentina.

1. Responsable del tratamiento
Sur Primicia — Portal digital de noticias con sede editorial en Río Grande, Tierra del Fuego, Antártida e Islas del Atlántico Sur, Argentina. Correo de contacto para asuntos de privacidad: redaccion@surprimicia.com.ar.

2. Datos que recopilamos
Recopilamos únicamente los datos necesarios para operar el portal y atender consultas:
- Datos de navegación: dirección IP, tipo de dispositivo, navegador, sistema operativo, páginas visitadas, tiempo de permanencia y fuente de referencia. Estos datos se obtienen mediante cookies y herramientas de analítica.
- Datos que aportás voluntariamente: nombre, correo electrónico, asunto y mensaje cuando escribís a través del formulario de contacto, o cualquier dato adicional que decidas enviarnos.
- Datos técnicos anónimos: métricas agregadas de audiencia (visitas, notas más leídas, categorías más consultadas), sin identificación individual.

3. Finalidades del tratamiento
Utilizamos los datos para: (a) prestar el servicio del portal y mejorar la experiencia de lectura; (b) medir audiencia y elaborar estadísticas agregadas; (c) responder consultas recibidas por el formulario de contacto; (d) prevenir abusos, fraude o usos indebidos del sitio; (e) cumplir obligaciones legales.

4. Cookies y tecnologías similares
Sur Primicia utiliza cookies propias y de terceros. En particular:
- Cookies técnicas necesarias para el funcionamiento del portal.
- Cookies de analítica de Google Analytics 4 (Google LLC), que permiten conocer cómo se utiliza el sitio de forma agregada. Podés consultar la política de privacidad de Google en https://policies.google.com/privacy y desactivar el seguimiento instalando el complemento oficial en https://tools.google.com/dlpage/gaoptout.
- Cookies de publicidad, sólo cuando corresponda, para servir avisos y medir campañas.
Podés bloquear o eliminar cookies desde la configuración de tu navegador. Algunas funciones del portal podrían verse afectadas si lo hacés.

5. Publicidad y terceros
El portal puede mostrar anuncios propios o servidos por terceros. Los proveedores de publicidad pueden utilizar cookies o identificadores anónimos con fines de medición. No compartimos con anunciantes datos personales identificables sin tu consentimiento.

6. Conservación de los datos
Los datos de navegación se conservan por el plazo mínimo necesario para cumplir con los fines descriptos y con las obligaciones legales. Los mensajes recibidos por el formulario de contacto se conservan mientras sean necesarios para gestionar la consulta y por los plazos que exija la normativa aplicable.

7. Derechos del titular de los datos (ARCO)
De acuerdo con la Ley 25.326, tenés derecho a acceder, rectificar, actualizar y suprimir tus datos personales, así como a oponerte al tratamiento. Podés ejercer estos derechos escribiendo a redaccion@surprimicia.com.ar. La Agencia de Acceso a la Información Pública (AAIP), en su carácter de órgano de control, tiene la atribución de atender denuncias y reclamos.

8. Seguridad
Adoptamos medidas técnicas y organizativas razonables para proteger los datos contra accesos no autorizados, pérdida o alteración. Ningún sistema es 100% infalible; ante un incidente actuaremos conforme a la normativa vigente.

9. Menores de edad
Los servicios del portal están dirigidos a un público general. Si tomamos conocimiento de que se recibieron datos de menores sin autorización de sus responsables legales, los eliminaremos.

10. Cambios en esta política
Podemos actualizar esta política para reflejar cambios legales, técnicos u operativos. La versión vigente será siempre la publicada en esta página, con su fecha de actualización.

11. Contacto
Para cualquier consulta sobre privacidad y protección de datos podés escribirnos a redaccion@surprimicia.com.ar o desde la página de contacto del portal.`;

export default function PrivacyPage() {
  const [content, setContent] = useState({
    title: DEFAULT_PRIVACY_TITLE,
    body: DEFAULT_PRIVACY_CONTENT,
  });

  useEffect(() => {
    api.siteSettings()
      .then((s) => {
        setContent({
          title: (s?.privacy_title && s.privacy_title.trim()) || DEFAULT_PRIVACY_TITLE,
          body: (s?.privacy_content && s.privacy_content.trim()) || DEFAULT_PRIVACY_CONTENT,
        });
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const paragraphs = content.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <div data-testid={TID.privacyPage} className="container-wide py-10">
      <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold mb-2">Institucional</div>
      <h1 className="font-serif font-black text-4xl sm:text-5xl text-blueblack">{content.title}</h1>
      <div className="mt-6 h-[3px] w-16 bg-watergreen" />

      <article
        data-testid="privacy-body"
        className="mt-10 max-w-3xl space-y-5 text-blueblack leading-[1.85] text-[16px]"
      >
        {paragraphs.map((p, i) => (
          <p key={i} className="whitespace-pre-line">{p}</p>
        ))}
        <p className="text-sm text-textgray pt-4">
          ¿Consultas sobre esta política? Escribinos desde nuestra{" "}
          <Link to="/contacto" className="text-petroleum hover:text-turquoise underline underline-offset-4">
            página de contacto
          </Link>
          .
        </p>
      </article>
    </div>
  );
}
